import React from "react";

/**
 * CopilotBotIcon — Vector rendition of the CalVant AI Bot logo.
 * Themed with CalVant's exact brand colors (matching "Start Tutorial" & CalVant Logo):
 * Gradient: from #667eea to #764ba2
 *
 * Props:
 *  - size: number (default 44)
 *  - breathing: boolean (default true) — enables the breathing/pulsing glow effect
 *  - isThinking: boolean (default false) — enhances animation when AI is processing
 *  - className: string — optional extra CSS classes
 */
export default function CopilotBotIcon({
  size = 44,
  breathing = true,
  isThinking = false,
  className = "",
  style = {},
}) {
  const animClass = isThinking
    ? "copilot-bot-thinking"
    : breathing
    ? "copilot-bot-breathing"
    : "";

  return (
    <div
      className={`copilot-bot-container relative inline-flex items-center justify-center flex-shrink-0 ${animClass} ${className}`}
      style={{
        width: size,
        height: size,
        ...style,
      }}
      aria-label="CalVant AI Copilot"
    >
      <svg
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-sm select-none"
      >
        <defs>
          {/* CalVant Brand Gradient (#667eea to #764ba2) */}
          <linearGradient id="calvantBrandGradient" x1="10" y1="10" x2="110" y2="110" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#667eea" />
            <stop offset="100%" stopColor="#764ba2" />
          </linearGradient>

          {/* Body Fill Gradient (Light, pristine modern style) */}
          <linearGradient id="calvantBodyFill" x1="60" y1="20" x2="60" y2="110" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#f3f4f9" stopOpacity="0.98" />
          </linearGradient>

          {/* Electric Cyan/Neon Glow for Eyes & Antenna */}
          <radialGradient id="calvantGlowEye" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="1" />
            <stop offset="65%" stopColor="#0ea5e9" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#667eea" stopOpacity="0.3" />
          </radialGradient>

          {/* Antenna Glow */}
          <radialGradient id="calvantAntennaGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="40%" stopColor="#667eea" />
            <stop offset="100%" stopColor="#764ba2" />
          </radialGradient>

          {/* Ambient Glow Filter */}
          <filter id="copilotGlowFilter" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ── 1. ANTENNA ── */}
        {/* Antenna Base Mount */}
        <path
          d="M54 36C54 34.5 56.5 33 60 33C63.5 33 66 34.5 66 36L64 39H56L54 36Z"
          fill="url(#calvantBrandGradient)"
        />
        {/* Antenna Stem */}
        <line
          x1="60"
          y1="33"
          x2="60"
          y2="20"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {/* Antenna Glowing Orb */}
        <circle
          cx="60"
          cy="15"
          r="6.5"
          fill="url(#calvantAntennaGlow)"
          filter="url(#copilotGlowFilter)"
          className="copilot-antenna-pulse"
        />
        <circle cx="58.5" cy="13.5" r="2" fill="#FFFFFF" opacity="0.95" />

        {/* ── 2. SIDE EARS / HEADPHONES ── */}
        {/* Left Ear */}
        <path
          d="M27 54C22.5 54 19 58 19 63.5V74.5C19 80 22.5 84 27 84"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="4.5"
          strokeLinecap="round"
        />
        {/* Right Ear */}
        <path
          d="M93 54C97.5 54 101 58 101 63.5V74.5C101 80 97.5 84 93 84"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="4.5"
          strokeLinecap="round"
        />

        {/* ── 3. MAIN HEAD / HELMET CONTOUR ── */}
        <rect
          x="26"
          y="38"
          width="68"
          height="60"
          rx="28"
          fill="url(#calvantBodyFill)"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="5"
        />

        {/* ── 4. FOREHEAD PANEL ACCENT LINE ── */}
        <path
          d="M42 45C53 43 67 43 78 45"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="3.5"
          strokeLinecap="round"
        />

        {/* ── 5. INNER VISOR / FACEPLATE ── */}
        <rect
          x="33"
          y="50"
          width="54"
          height="32"
          rx="16"
          fill="#1e1b4b"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="4"
        />

        {/* ── 6. GLOWING ROUND EYES ── */}
        {/* Left Eye */}
        <g className="copilot-eye-glow">
          <circle
            cx="47"
            cy="66"
            r="6.5"
            fill="url(#calvantGlowEye)"
            filter="url(#copilotGlowFilter)"
          />
          {/* Specular Eye Highlight */}
          <circle cx="45" cy="64" r="2" fill="#FFFFFF" opacity="0.9" />
        </g>

        {/* Right Eye */}
        <g className="copilot-eye-glow">
          <circle
            cx="73"
            cy="66"
            r="6.5"
            fill="url(#calvantGlowEye)"
            filter="url(#copilotGlowFilter)"
          />
          {/* Specular Eye Highlight */}
          <circle cx="71" cy="64" r="2" fill="#FFFFFF" opacity="0.9" />
        </g>

        {/* ── 7. CHIN / LOWER JAWLINE ACCENT ── */}
        <path
          d="M45 88C54 92 66 92 75 88"
          stroke="url(#calvantBrandGradient)"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
